/**
 * One-shot, in-memory "the user asked to set up 2FA" signal.
 *
 * Bridges the dashboard "Set up 2FA" nudge (`MfaEncourageBanner`, 7 call
 * sites across admin/billing/worker/supervisor/operations) navigating to
 * Account with `TwoFactorSettingsRow` auto-opening `MfaSetupModal` once it
 * mounts there. Deliberately NOT a URL param (2FA enrollment state is a
 * security surface, not a shareable/bookmarkable view) and NOT
 * localStorage (must never survive past the triggering navigation, and must
 * never leak to the next user on a shared device — see
 * `clearPerUserDeviceState`). A module-level timestamp does both jobs: it's
 * gone the instant it's read, and it's gone at sign-out regardless.
 */

const INTENT_TTL_MS = 15000;

let requestedAt = null;
const listeners = new Set();

/**
 * Call right before navigating to Account — stamps "now" AND notifies every
 * subscriber. The notify is what reaches a `TwoFactorSettingsRow` that is
 * ALREADY mounted (keep-alive Account tab): its mount-time consume ran long
 * ago, so without a push the later banner tap would never be seen. The stamp
 * is what reaches a row that mounts AFTER this call.
 */
export function requestMfaSetup() {
  requestedAt = Date.now();
  Array.from(listeners).forEach((cb) => {
    try { cb(); } catch (_e) { /* one bad listener never blocks the others */ }
  });
}

/**
 * Subscribe to future `requestMfaSetup()` calls. The callback carries no
 * payload — the subscriber must still `consumeMfaSetupIntent()`, which is
 * what keeps each request handled at most once. Returns an unsubscribe.
 */
export function subscribeMfaSetupIntent(cb) {
  if (typeof cb !== 'function') return () => {};
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/**
 * Reads AND clears the pending intent — true at most once per
 * `requestMfaSetup()` call, and only within `INTENT_TTL_MS` of it. A second
 * call (e.g. a remount) always reads false, even if the first call happened
 * a moment ago and answered true.
 */
export function consumeMfaSetupIntent() {
  const at = requestedAt;
  requestedAt = null;
  if (at == null) return false;
  return Date.now() - at <= INTENT_TTL_MS;
}

/** Sign-out hook (`clearPerUserDeviceState`) — a pending intent must never survive a user switch. */
export function clearMfaSetupIntent() {
  requestedAt = null;
}
